import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Order } from '../types';
import { ordersService } from '../services/ordersService';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Edit, Printer, Package, ExternalLink, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { useCurrencyFormat } from '../hooks/useCurrencyFormat';
import CustomerReferenceField from '../visitors/CustomerReferenceField';
import AddressField from '../visitors/AddressField';
import { PrintBillButton } from './components/PrintBillButton';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { useTableStatusStore } from '../stores/useTableStatusStore';
import { nekoPos } from 'public-contracts';

// Product cache to avoid repeated API calls
const productCache = new Map<string, any>();

const ProductNameDisplay = ({ productId }: { productId: string | number }) => {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(!productCache.has(String(productId)));
  const [product, setProduct] = useState(productCache.get(String(productId)));
  const [error, setError] = useState<Error | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!productId) return;

    const productIdStr = String(productId);

    // If we have it in cache, use it
    if (productCache.has(productIdStr)) {
      setProduct(productCache.get(productIdStr));
      setLoading(false);
      return;
    }

    // Otherwise fetch it
    setLoading(true);
    setError(null);

    authenticatedFetch(`/api/products/${productIdStr}`)
      .then((response) => response.json())
      .then((productData) => {
        if (isMounted.current) {
          // Store in cache
          productCache.set(productIdStr, productData);
          setProduct(productData);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted.current) {
          console.error('Error loading product:', err);
          setError(err);
          setLoading(false);
        }
      });
  }, [productId]);

  if (loading) {
    return (
      <span className="text-sm text-muted-foreground animate-pulse">
        Loading...
      </span>
    );
  }

  if (error || !product) {
    return (
      <span className="text-sm text-muted-foreground">
        {productId || 'Unknown Product'}
      </span>
    );
  }

  return (
    <div className="space-y-1">
      <p className="font-medium">{product.name || productId}</p>
      {product.reference && (
        <p className="text-xs text-muted-foreground">Ref: {product.reference}</p>
      )}
      {product.categoryId && (
        <p className="text-xs text-muted-foreground">Category ID: {product.categoryId}</p>
      )}
      {product.description && (
        <p className="text-xs text-muted-foreground italic">{product.description}</p>
      )}
      <p className="text-xs text-muted-foreground">Product ID: {productId}</p>
    </div>
  );
};

export const ModernOrderShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const { formatCurrency } = useCurrencyFormat();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { tableStatuses, fetchTableStatuses } = useTableStatusStore();
  const [tableStatus, setTableStatus] = useState<nekoPos.TableStatus | null>(
    null
  );
  const [suborders, setSuborders] = useState<Order[]>([]);
  const [loadingSuborders, setLoadingSuborders] = useState(false);
  const [expandedSuborders, setExpandedSuborders] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        setError(null);
        const orderData = (await ordersService.getOrder(id)) as Order;
        setOrder(orderData);

        // Fetch table statuses to check if table is booked by this order
        await fetchTableStatuses();
      } catch (err) {
        console.error('Error fetching order:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch order');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [id, fetchTableStatuses]);

  // Fetch suborders if this is a parent order
  useEffect(() => {
    const fetchSuborders = async () => {
      if (!order || (order as any).parentOrderId) {
        setSuborders([]);
        return;
      }

      setLoadingSuborders(true);
      try {
        let orderSuborders: Order[] = [];
        let page = 1;
        const perPage = 1000;
        let hasMore = true;

        while (hasMore) {
          const ordersResponse = await ordersService.getOrders({
            pagination: { page, perPage },
            sort: { field: 'date', order: 'DESC' },
            filter: {},
          });

          const pageSuborders = (ordersResponse.data || []).filter(
            (o: Order) => (o as any).parentOrderId === order.id
          );
          orderSuborders = orderSuborders.concat(pageSuborders);

          if (
            ordersResponse.data.length < perPage ||
            orderSuborders.length >= (ordersResponse.total || 0)
          ) {
            hasMore = false;
          } else {
            page++;
          }
        }

        setSuborders(orderSuborders);
      } catch (error) {
        console.error('Error fetching suborders:', error);
        setSuborders([]);
      } finally {
        setLoadingSuborders(false);
      }
    };

    if (order) {
      fetchSuborders();
    }
  }, [order]);

  const toggleSuborderExpansion = (suborderId: string) => {
    setExpandedSuborders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(suborderId)) {
        newSet.delete(suborderId);
      } else {
        newSet.add(suborderId);
      }
      return newSet;
    });
  };

  const handleDeleteSuborder = async (suborderId: string) => {
    try {
      const suborder = suborders.find((s) => s.id === suborderId);
      if (!suborder) return;

      // Check if suborder has items
      if ((suborder.items || []).length > 0) {
        alert(
          translate(
            'pos.orders.messages.cannotDeleteSuborderWithItems',
            'Cannot delete suborder with items. Please remove all items first.'
          )
        );
        return;
      }

      // Confirm deletion
      if (
        !window.confirm(
          translate(
            'pos.orders.messages.confirmDeleteSuborder',
            'Are you sure you want to delete this suborder? This action cannot be undone.'
          )
        )
      ) {
        return;
      }

      // Delete the suborder
      await ordersService.deleteOrder(suborderId);

      alert(
        translate(
          'pos.orders.messages.suborderDeleted',
          'Suborder deleted successfully'
        )
      );

      // Remove from local state
      setSuborders((prev) => prev.filter((s) => s.id !== suborderId));

      // Refresh the current order to get updated data
      if (id) {
        const updatedOrder = await ordersService.getOrder(id);
        setOrder(updatedOrder as Order);
      }
    } catch (error: any) {
      console.error('Error deleting suborder:', error);
      alert(
        error?.message ||
          translate(
            'pos.orders.messages.errorDeletingSuborder',
            'Failed to delete suborder'
          )
      );
    }
  };

  // Find table status for this order's table
  useEffect(() => {
    if (order?.tableNumber && tableStatuses.length > 0) {
      const status = tableStatuses.find(
        (ts) => ts.tableNumber === order.tableNumber
      );
      setTableStatus(status || null);
    } else {
      setTableStatus(null);
    }
  }, [order?.tableNumber, tableStatuses]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600">
            Error loading order
          </h2>
          <p className="text-gray-600">
            The order could not be found or loaded.
          </p>
          <Button
            onClick={() => navigate('/orders')}
            className="mt-4"
            variant="outline"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Orders
          </Button>
        </div>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    ordered: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    ready:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    delivered:
      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  };

  const orderTypeLabels: Record<string, string> = {
    dine_in: translate('pos.orders.orderTypes.dineIn'),
    takeaway: translate('pos.orders.orderTypes.takeaway'),
    delivery: translate('pos.orders.orderTypes.delivery'),
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/orders')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {translate('pos.common.action.back')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {translate('pos.orders.show.title', 'Order')} #{order.reference}
            </h1>
            <p className="text-muted-foreground">{order.reference}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/orders/${order.id}/edit`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            {translate('pos.common.action.edit')}
          </Button>
          {order.status !== 'cancelled' && (
            <PrintBillButton orderId={order.id.toString()} />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Information */}
          <Card>
            <CardHeader>
              <CardTitle>
                {translate(
                  'pos.orders.fields.orderInformation',
                  'Order Information'
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {translate('pos.orders.fields.reference')}
                  </label>
                  <p className="text-lg font-mono">{order.reference}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {translate('pos.orders.fields.date')}
                  </label>
                  <p>
                    {new Date(order.date).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {translate('pos.orders.fields.status')}
                  </label>
                  <div>
                    <Badge
                      className={
                        statusColors[order.status] ||
                        'bg-gray-100 text-gray-800'
                      }
                    >
                      {translate(`pos.orders.tabs.${order.status}`)}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {translate('pos.orders.fields.orderType')}
                  </label>
                  <p>
                    {order.orderType
                      ? orderTypeLabels[order.orderType] || order.orderType
                      : '-'}
                  </p>
                </div>
                {order.tableNumber && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {translate('pos.orders.fields.tableNumber')}
                    </label>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{order.tableNumber}</p>
                      {tableStatus && (
                        <>
                          {tableStatus.orderId === order.id ? (
                            <Badge className="bg-blue-100 text-blue-800">
                              {translate(
                                'pos.orders.modern.tableBookedByThisOrder',
                                'Booked by this order'
                              )}
                            </Badge>
                          ) : tableStatus.orderId ? (
                            <Badge className="bg-orange-100 text-orange-800">
                              {translate(
                                'pos.orders.modern.tableBookedByOtherOrder',
                                'Booked by another order'
                              )}
                            </Badge>
                          ) : tableStatus.isAvailable ? (
                            <Badge className="bg-green-100 text-green-800">
                              {translate(
                                'pos.orders.modern.tableAvailable',
                                'Available'
                              )}
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-800">
                              {translate(
                                'pos.orders.modern.tableUnavailable',
                                'Unavailable'
                              )}
                            </Badge>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {translate('pos.orders.fields.paid')}
                  </label>
                  <p>
                    {order.paid ? (
                      <Badge className="bg-green-100 text-green-800">
                        {translate('pos.common.message.yes', 'Yes')}
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        {translate('pos.common.message.no', 'No')}
                      </Badge>
                    )}
                  </p>
                </div>
                {(order as any).returned && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {translate(
                        'pos.orders.modern.returned',
                        'Returned'
                      )}
                    </label>
                    <div>
                      <Badge className="bg-orange-100 text-orange-800">
                        {translate('pos.common.action.yes')}
                      </Badge>
                    </div>
                  </div>
                )}
                {(order as any).sourcePlatform && (order as any).sourcePlatform !== 'internal' && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Platform
                    </label>
                    <div>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        {(() => {
                          const platform = (order as any).sourcePlatform?.toLowerCase();
                          const platformMap: Record<string, string> = {
                            grab: 'Grab',
                            shopee: 'Shopee',
                            xanhsm: 'Xanh SM',
                            xanh_sm: 'Xanh SM',
                          };
                          return platformMap[platform] || platform;
                        })()}
                      </Badge>
                    </div>
                  </div>
                )}
                {(order as any).parentOrderId && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Parent Order
                    </label>
                    <div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/orders/${(order as any).parentOrderId}/edit`)}
                        className="h-8"
                      >
                        <ExternalLink className="h-3 w-3 mr-1.5" />
                        {translate('pos.orders.actions.viewParentOrder', 'View Parent Order')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle>
                {translate('pos.orders.fields.customer')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {order.customerId ? (
                <>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {translate('pos.orders.fields.customer')}
                    </label>
                    <div className="mt-1">
                      <CustomerReferenceField
                        customerId={order.customerId}
                        showPoints={true}
                        showZalo={true}
                        sourcePlatform={(order as any).sourcePlatform}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {translate('pos.orders.fields.address')}
                    </label>
                    <div className="mt-1">
                      <AddressField customerId={order.customerId} />
                    </div>
                  </div>
                </>
              ) : (order as any).sourcePlatform &&
                (order as any).sourcePlatform !== 'internal' ? (
                <div>
                  <Badge
                    variant="outline"
                    className="bg-green-50 text-green-700 border-green-200"
                  >
                    {(() => {
                      const platform = (order as any).sourcePlatform?.toLowerCase();
                      const platformMap: Record<string, string> = {
                        grab: 'Grab',
                        shopee: 'Shopee',
                        xanhsm: 'Xanh SM',
                        xanh_sm: 'Xanh SM',
                      };
                      return platformMap[platform] || platform;
                    })()}
                  </Badge>
                </div>
              ) : (
                <p className="text-muted-foreground italic">
                  {translate('pos.orders.modern.walkInCustomer')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Items */}
          <Card>
            <CardHeader>
              <CardTitle>
                {translate('pos.orders.fields.items')} (
                {order.items?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {order.items && order.items.length > 0 ? (
                <div className="space-y-3">
                  {order.items.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start justify-between p-3 border rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        {item.productId ? (
                          <ProductNameDisplay productId={item.productId} />
                        ) : (
                          <p className="font-medium">Item {index + 1}</p>
                        )}
                        {item.note && (
                          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                            <p className="text-sm text-yellow-800">
                              <span className="font-medium">Note: </span>
                              {item.note}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="text-right ml-4 flex-shrink-0">
                        <p className="font-medium">
                          {item.quantity} ×{' '}
                          <span data-sensitive-field="amount">
                            {formatCurrency(item.price)}
                          </span>
                        </p>
                        <p className="text-lg font-semibold text-green-600">
                          <span data-sensitive-field="amount">
                            {formatCurrency(item.price * item.quantity)}
                          </span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground italic">
                  {translate('pos.orders.messages.noItems')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Suborders Section - only show for parent orders */}
          {!order.parentOrderId && (suborders.length > 0 || loadingSuborders) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {translate('pos.orders.fields.suborders', 'Suborders')}
                  {suborders.length > 0 && ` (${suborders.length})`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingSuborders ? (
                  <p className="text-gray-500">
                    {translate('pos.common.message.loading', 'Loading...')}
                  </p>
                ) : suborders.length === 0 ? (
                  <p className="text-gray-500">
                    {translate('pos.orders.messages.noSuborders', 'No suborders yet')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {suborders.map((suborder) => {
                      const isExpanded = expandedSuborders.has(suborder.id);
                      const suborderTotal = (suborder.items || []).reduce(
                        (sum, item) => sum + (item.price || 0) * (item.quantity || 0),
                        0
                      );

                      return (
                        <div
                          key={suborder.id}
                          className="border rounded-lg overflow-hidden"
                        >
                          {/* Suborder Header */}
                          <div
                            className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleSuborderExpansion(suborder.id)}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4 text-gray-500" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-gray-500" />
                              )}
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{suborder.reference}</p>
                                  <Badge
                                    className={
                                      suborder.status === 'ordered'
                                        ? 'bg-blue-100 text-blue-800'
                                        : suborder.status === 'ready'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : suborder.status === 'delivered'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                    }
                                  >
                                    {translate(
                                      `pos.orders.tabs.${suborder.status}`,
                                      suborder.status
                                    )}
                                  </Badge>
                                </div>
                                <p className="text-sm text-gray-500">
                                  {suborder.items?.length || 0}{' '}
                                  {translate('pos.orders.fields.items', 'items')}
                                  {' • '}
                                  <span data-sensitive-field="amount">
                                    {formatCurrency(suborderTotal)}
                                  </span>
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {(suborder.items || []).length === 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSuborder(suborder.id);
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/orders/${suborder.id}/edit`);
                                }}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Expanded Items List */}
                          {isExpanded && suborder.items && suborder.items.length > 0 && (
                            <div className="border-t bg-gray-50 p-3 space-y-2">
                              {suborder.items.map((item, index) => (
                                <div
                                  key={index}
                                  className="flex items-start justify-between p-2 bg-white rounded border"
                                >
                                  <div className="flex-1 min-w-0">
                                    <ProductNameDisplay productId={item.productId} />
                                    {item.note && (
                                      <p className="text-xs text-gray-500 mt-1 italic">
                                        {item.note}
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-right ml-4">
                                    <p className="text-sm text-gray-700">
                                      {item.quantity} ×{' '}
                                      <span data-sensitive-field="amount">
                                        {formatCurrency(item.price || 0)}
                                      </span>
                                    </p>
                                    <p className="font-medium text-gray-900">
                                      <span data-sensitive-field="amount">
                                        {formatCurrency((item.price || 0) * (item.quantity || 0))}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle>
                {translate('pos.orders.fields.total')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(() => {
                // Use the same calculation logic as edit view (calculateTotals)
                const items = order.items || [];
                const calculatedTotalExTaxes = items.reduce(
                  (sum: number, item: any) => {
                    const quantity = Number(item.quantity) || 0;
                    const price = Number(item.price) || 0;
                    return sum + quantity * price;
                  },
                  0
                );

                const pointsRedemption =
                  Number((order as any).pointsRedemption) || 0;
                const taxes = Number(order.taxes) || 0;
                const deliveryFees = Number(order.deliveryFees) || 0;
                const totalExTaxes =
                  Number(order.totalExTaxes) || calculatedTotalExTaxes || 0;

                // Calculate discount (promotion discount)
                // Discount = totalExTaxes - (total - taxes - deliveryFees + pointsRedemption)
                // Or simpler: if totalExTaxes > (total + taxes + deliveryFees - pointsRedemption), there's a discount
                const orderTotal = Number(order.total);
                let discount = 0;
                if (orderTotal > 0 && totalExTaxes > 0) {
                  // Reverse calculate: discount = totalExTaxes - (total - taxes - deliveryFees + pointsRedemption)
                  const expectedSubtotal =
                    orderTotal - taxes - deliveryFees + pointsRedemption;
                  if (totalExTaxes > expectedSubtotal) {
                    discount = totalExTaxes - expectedSubtotal;
                  }
                }

                // Use same calculation logic as edit view (calculateTotals)
                // calculateTotals: total = totalExTaxes + taxes + deliveryFees - discount
                // Points redemption is shown separately but doesn't affect the total calculation in calculateTotals
                // So we use: total = totalExTaxes + taxes + deliveryFees - discount

                // Use order.total if it exists and is valid (> 0), otherwise calculate from items
                let calculatedTotal = orderTotal;

                // If order.total is 0 or missing, calculate it from items (same as calculateTotals)
                if (!calculatedTotal || calculatedTotal === 0) {
                  // Calculate using same logic as calculateTotals
                  // Since we don't have promotion info, assume no discount for now
                  // total = totalExTaxes + taxes + deliveryFees - discount
                  // Without discount: total = totalExTaxes + taxes + deliveryFees
                  calculatedTotal = totalExTaxes + taxes + deliveryFees;
                  // Ensure total is not negative
                  calculatedTotal = Math.max(0, calculatedTotal);
                }

                // Calculate discount: if totalExTaxes + taxes + deliveryFees > total, there's a discount
                // This accounts for both promotion discount and points redemption
                if (totalExTaxes > 0 && calculatedTotal > 0) {
                  const totalBeforeDiscount =
                    totalExTaxes + taxes + deliveryFees;
                  if (totalBeforeDiscount > calculatedTotal) {
                    discount = totalBeforeDiscount - calculatedTotal;
                  }
                }

                return (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {translate(
                          'pos.orders.fields.totalBeforeDiscount',
                          'Subtotal'
                        )}
                      </span>
                      <span>
                        <span data-sensitive-field="amount">
                          {formatCurrency(totalExTaxes)}
                        </span>
                      </span>
                    </div>
                    {/* Points Redemption Discount */}
                    {pointsRedemption > 0 && (
                      <div className="flex justify-between text-sm text-red-600">
                        <span>
                          {translate(
                            'pos.orders.fields.pointsRedemption',
                            'Points Used'
                          )}
                        </span>
                        <span>
                          -
                          <span data-sensitive-field="amount">
                            {formatCurrency(pointsRedemption)}
                          </span>
                        </span>
                      </div>
                    )}
                    {/* Promotion Discount - if exists */}
                    {discount > 0 && (
                      <div className="flex justify-between text-sm text-red-600">
                        <span>
                          {translate(
                            'pos.orders.fields.discount',
                            'Discount'
                          )}
                        </span>
                        <span>
                          -
                          <span data-sensitive-field="amount">
                            {formatCurrency(discount)}
                          </span>
                        </span>
                      </div>
                    )}
                    {taxes > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {translate('pos.orders.fields.taxes', 'Taxes')}{' '}
                          ({order.taxRate || 0}%)
                        </span>
                        <span>
                          <span data-sensitive-field="amount">
                            {formatCurrency(taxes)}
                          </span>
                        </span>
                      </div>
                    )}
                    {deliveryFees > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {translate(
                            'pos.orders.fields.deliveryFees',
                            'Delivery Fees'
                          )}
                        </span>
                        <span>
                          <span data-sensitive-field="amount">
                            {formatCurrency(deliveryFees)}
                          </span>
                        </span>
                      </div>
                    )}
                    <div className="border-t pt-3 mt-2 flex justify-between items-center">
                      <span className="text-sm font-medium">
                        {translate(
                          'pos.orders.fields.totalAfterDiscount',
                          'Total'
                        )}
                      </span>
                      <span className="text-2xl font-bold text-green-600">
                        <span data-sensitive-field="amount">
                          {formatCurrency(calculatedTotal)}
                        </span>
                      </span>
                    </div>
                  </>
                );
              })()}
              
              {/* Suborders Total Breakdown */}
              {suborders.length > 0 && (() => {
                // Calculate parent order total
                const items = order.items || [];
                const calculatedTotalExTaxes = items.reduce(
                  (sum: number, item: any) => {
                    const quantity = Number(item.quantity) || 0;
                    const price = Number(item.price) || 0;
                    return sum + quantity * price;
                  },
                  0
                );
                const taxes = Number(order.taxes) || 0;
                const deliveryFees = Number(order.deliveryFees) || 0;
                const totalExTaxes = Number(order.totalExTaxes) || calculatedTotalExTaxes || 0;
                const orderTotal = Number(order.total) || 0;
                let parentTotal = orderTotal;
                if (!parentTotal || parentTotal === 0) {
                  parentTotal = totalExTaxes + taxes + deliveryFees;
                  parentTotal = Math.max(0, parentTotal);
                }
                
                // Recalculate discount for parent
                if (totalExTaxes > 0 && parentTotal > 0) {
                  const totalBeforeDiscount = totalExTaxes + taxes + deliveryFees;
                  if (totalBeforeDiscount > parentTotal) {
                    parentTotal = totalBeforeDiscount - (totalBeforeDiscount - parentTotal);
                  }
                }
                
                const suborderTotal = suborders.reduce((sum, suborder) => {
                  return sum + ((suborder.items || []).reduce((itemSum: number, item: any) => {
                    const price = typeof item.price === 'number' ? item.price : 0;
                    const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
                    return itemSum + price * quantity;
                  }, 0));
                }, 0);
                
                const combinedTotal = parentTotal + suborderTotal;
                
                return (
                  <div className="pt-4 border-t mt-4 space-y-2">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {translate('pos.orders.fields.totalBreakdown', 'Total Breakdown')}
                    </h4>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {translate('pos.orders.fields.parentOrder', 'Parent Order')}:
                      </span>
                      <span className="font-medium">
                        <span data-sensitive-field="amount">
                          {formatCurrency(parentTotal)}
                        </span>
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {translate('pos.orders.fields.suborders', 'Suborders')} ({suborders.length}):
                      </span>
                      <span className="font-medium">
                        <span data-sensitive-field="amount">
                          {formatCurrency(suborderTotal)}
                        </span>
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 mt-2 border-t border-gray-300">
                      <span className="font-semibold text-gray-900">
                        {translate('pos.orders.fields.combinedTotal', 'Combined Total')}:
                      </span>
                      <span className="text-xl font-bold text-green-600">
                        <span data-sensitive-field="amount">
                          {formatCurrency(combinedTotal)}
                        </span>
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 pt-1">
                      {translate('pos.orders.fields.totalItems', 'Total Items')}:{' '}
                      {(order.items?.length || 0) + suborders.reduce((sum, suborder) => sum + (suborder.items?.length || 0), 0)} items
                    </div>
                  </div>
                );
              })()}
              
              {/* Points Earned */}
              {(order as any).pointsEarned > 0 && (
                <div className="pt-2 border-t mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {translate(
                        'pos.orders.fields.pointsEarned',
                        'Points Earned'
                      )}
                    </span>
                    <span className="font-medium text-blue-600">
                      +{(order as any).pointsEarned}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ModernOrderShow;
